<template>
  <div>
    <div class="main">
      <oie-chat-greeting
        v-if="showGreeting"
        :close="handleCloseGreeting">
      </oie-chat-greeting>

      <button
        v-if="!isChatbotVisible"
        class="floating-button"
        :aria-label="tStatic('caiAccessibilityOpenChatInfoText')"
        :title="tStatic('caiFloatingButtonText')"
        @click="toggleChatbot">
        <span v-if="hasUnreadMessages" class="unread-indicator" aria-hidden="true"></span>
        <oie-use-svg svg="message" height="28" width="28"></oie-use-svg>
      </button>

      <div
        v-if="isChatbotVisible"
        class="chatbot"
        role="dialog"
        aria-labelledby="chatbot-header"
        @keydown="handleEscapeKeyOnChatbot">
        <div id="chatbot-header" class="header">
          <div class="logo-title-wrapper">
            <h5 id="chatbot-title">
              {{ tStatic('caiTitle') }}
            </h5>
          </div>
          <div class="actions">
            <button
              type="button"
              :aria-label="tStatic('caiAccessibilityMinimizeChatInfoText')"
              :title="tStatic('genericMinimize')"
              @click="toggleChatbot">
              <oie-use-svg
                svg="minimize"
                color="#fff"
                width="20"
                height="20">
              </oie-use-svg>
            </button>
            <button
              type="button"
              :aria-label="tStatic('caiAccessibilityCloseChatInfoText')"
              :title="tStatic('genericClose')"
              @click="handleClose">
              <oie-use-svg
                svg="close-cross"
                color="#fff"
                width="20"
                height="20">
              </oie-use-svg>
            </button>
          </div>
        </div>

        <div ref="messageContainer" class="body">
          <oie-clear-chat-bot-popup
            :is-visible="isClearChatDialogVisible"
            :handle-cancel="handleCancel"
            :handle-clear="handleClear">
          </oie-clear-chat-bot-popup>
          <div
            v-for="(message, index) in messages"
            :key="`msg_${index}`"
            class="message-container">
            <template v-if="message.sender === 'bot'">
              <div
                v-if="message.isTyping"
                class="message response loading-text"
                aria-live="polite">
                <span class="typing-dots" aria-hidden="true">
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                </span>
                <span>{{ tStatic('caiRespondingText') }}</span>
              </div>
              <div v-else class="message response">
                <div aria-live="polite" v-html="message.text"></div>
                <template v-for="(option, optionIndex) in message.options">
                  <div v-if="option" :key="`option_${optionIndex}`">
                    <div v-if="option.entityType === 1" class="message-options">
                      <oie-product-option
                        v-for="(entity, entityIndex) in option.entities"
                        :key="`entity_${entityIndex}`"
                        :session-id="currentSessionId"
                        :content-url="entity.contentUrl"
                        :part-name="entity.partName"
                        :part-number="entity.partNumber"
                        :stock="entity.stock"
                        :currency="entity.currency"
                        :region="entity.region"
                        :is-quote-item="entity.isQuoteItem"
                        :price="entity.price"
                        :vat-included="entity.includingVat"
                        :stock-status="entity.stockStatusCss"
                        :stock-message="entity.stockMessage"
                        aria-label="Product option">
                      </oie-product-option>
                    </div>
                    <div v-else-if="option.entityType === 2" class="message-options">
                      <oie-webcontent-option
                        v-for="(entity, entityIndex) in option.entities"
                        :key="`entity_${entityIndex}`"
                        :session-id="currentSessionId"
                        :url="entity.url"
                        aria-label="Web content option">
                      </oie-webcontent-option>
                    </div>
                  </div>
                </template>
              </div>
            </template>
            <template v-else>
              <div class="message prompt" aria-live="polite" v-html="message.text"></div>
            </template>
          </div>
        </div>

        <div class="footer">
          <div class="input-field">
            <label for="chat-input" class="visually-hidden">{{ tStatic('caiInputPlaceholder') }}</label>
            <textarea
              id="chat-input"
              ref="text-area"
              v-model="newMessage"
              type="text"
              class="message-input"
              :placeholder="tStatic('caiInputPlaceholder')"
              :disabled="isTextFieldDisable"
              aria-label="Chat input"
              @keydown.exact.enter.prevent="sendMessage">
            </textarea>
            <oie-action-link
              class="send-button"
              :disabled="isTextFieldDisable"
              :aria-label="tStatic('caiAccessibilitySendMessageInfoText')"
              :title="tStatic('genericSend')"
              @click="sendMessage">
              <oie-use-svg
                svg="send"
                width="30"
                height="30"
                :color="focusColor">
              </oie-use-svg>
            </oie-action-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import httpService from '@/core/http-service'
import { v4 as uuidv4 } from 'uuid'
import gtm from '@/core/enhanced-ecommerce-gtm'

export default {
  data() {
    return {
      isChatbotVisible: false,
      isChatbotFocused: false,
      isClearChatDialogVisible: false,
      isTextFieldDisable: false,
      showGreeting: !this.hasGreetingCookie(),
      messages: [],
      newMessage: '',
      focusColor: '#ccc',
      cancelTokenSource: null,
      currentSessionId: null,
      hasUnreadMessages: false
    }
  },
  beforeCreate() {
    this.cancelToken = null
  },
  mounted() {
    this.loadMessages()
    if (this.messages.length > 1) {
      this.currentSessionId = localStorage.getItem('sessionId')
    }

    window.addEventListener('storage', this.handleStorageChange)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange)
  },
  updated() {
    if(this.newMessage !== '') {
      this.focusColor = '#0073bb'
    } else {
      this.focusColor = '#ccc'
    }
  },
  methods: {
    hasGreetingCookie() {
      return document.cookie.split(';').some(item => item.trim().startsWith('chatGreetingClosed=true'))
    },
    setGreetingCookie() {
      document.cookie = `chatGreetingClosed=true; path=/`
    },
    toggleChatbot() {
      this.isChatbotVisible = !this.isChatbotVisible
      if (this.isChatbotVisible) {
        this.showGreeting = false
        this.setGreetingCookie()
        this.scrollToBottom()
        this.hasUnreadMessages = false
        this.$nextTick(() => {
          this.$refs['text-area'].focus()
        })
      }
    },
    handleCloseGreeting() {
      this.showGreeting = false
      this.setGreetingCookie()
    },
    handleClose() {
      if (this.messages.length <= 1) {
        this.isChatbotVisible = false
        return
      }

      this.isClearChatDialogVisible = true
    },
    handleCancel() {
      this.isClearChatDialogVisible = false
    },
    handleClear() {
      if (this.cancelToken) {
        this.cancelToken()
      }

      if (this.currentSessionId) {
         // track no conversion if the chat session is cleared
        const chatConversion = localStorage.getItem('chat_conversion')
        if(!chatConversion){
          gtm.chatNoConversion(this.currentSessionId)
        }else{
          const chatConversionData = JSON.parse(chatConversion)
          if(chatConversionData && chatConversionData.chatSessionId != this.currentSessionId) {
            gtm.chatNoConversion(this.currentSessionId)
          }
        }
        localStorage.removeItem('chat_conversion')

        gtm.chatSessionEnd(this.currentSessionId, this.messages.length, 'clear')
      }

      this.clearChatHistory()
      this.isChatbotVisible = false
      this.isClearChatDialogVisible = false
    },
    handleEscapeKeyOnChatbot(event) {
      if(event.key === 'Escape' || event.keyCode === 27) {
        this.isChatbotVisible = false
      }
    },
    async send(requestBody) {
      try {
        this.cancelToken = httpService.createCancellationToken()
        const response = await httpService.post(
          'cai/chat',
          requestBody,
          {},
          {},
          this.cancelToken.cancelTokenSource
        )
        return { result: response.data, isCancelled: false }
      } catch (error) {
        console.log('An error occurred: ' + error)
        return { result: null, isCancelled: httpService.isCancellationError(error) }
      } finally {
        this.cancelToken = null
      }
    },
    async sendMessage() {
      if (this.newMessage.trim() === '') {
        this.$refs['text-area'].value = ''
        return
      }

      this.isTextFieldDisable = true

      let dialogId = uuidv4()
      let sessionId = localStorage.getItem('sessionId')

      if (sessionId === null) {
        sessionId = this.createSessionId()
        this.currentSessionId = sessionId
        gtm.chatSessionStart(sessionId)
      }

      this.messages.push({ dialogId: dialogId, sender: 'user', text: this.newMessage })
      this.scrollToBottom()

      const typingMessage = { sender: 'bot', text: '', isTyping: true }
      this.messages.push(typingMessage)
      this.scrollToBottom()

      const requestBody = {
        sessionId: sessionId,
        dialogId: dialogId,
        message: this.newMessage
      }

      const botResponse = await this.send(requestBody)
      await gtm.chatPrompt(this.currentSessionId, this.newMessage)

      this.isTextFieldDisable = false
      this.messages.pop()
      this.newMessage = ''

      if (botResponse.result) {
        const botResponseResult = botResponse.result
        const responseMessage = {
          dialogId: dialogId,
          sender: 'bot',
          text: botResponseResult.response.message,
          options: botResponseResult.response.options.map(entity => {
            return {
              entityType: entity.entityType,
              entities: entity.entities
            }
          })
        }
        this.messages.push(responseMessage)
        if(!this.isChatbotVisible) {
          this.hasUnreadMessages = true
        }

        const isProductRecommended = responseMessage.options && 
          responseMessage.options.some(option => option.entityType === 1)

        // track no conversion if no product was recommended
        if(isProductRecommended){
          gtm.chatResponseOutcome(this.currentSessionId, 'product_recommended')
        }else{
          gtm.chatResponseOutcome(this.currentSessionId, 'no_recommendation')
        }
      } else if (botResponse.isCancelled) {
        this.clearChatHistory()
        return
      } else {
        const errorMessage = this.tStatic('caiErrorResponse')
        this.messages.push({
          dialogId: dialogId,
          sender: 'bot',
          text: errorMessage
        })
      }

      this.saveMessages()
      this.scrollToBottom()

      this.$nextTick(() => {
        this.$refs['text-area'].focus()
      })
    },
    saveMessages() {
      const messagesToSave = this.messages.filter(message => !!message.text)
      localStorage.setItem('chatHistory', JSON.stringify(messagesToSave))
    },
    loadMessages() {
      const savedMessages = localStorage.getItem('chatHistory')
      if (savedMessages) {
        this.messages = JSON.parse(savedMessages)
      } else {
        this.messages.push({
          dialogId: null,
          sender: 'bot',
          text: this.tStatic('caiInitialChatMessage')
        })
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const messageContainer = this.$refs.messageContainer
        messageContainer.scrollTop = messageContainer.scrollHeight
      })
    },
    createSessionId() {
      const sessionId = uuidv4()
      localStorage.setItem('sessionId', sessionId)

      return sessionId
    },
    clearChatHistory() {
      localStorage.removeItem('sessionId')
      localStorage.removeItem('chatHistory')
      this.messages = []
      this.messages.push({
        dialogId: null,
        sender: 'bot',
        text: this.tStatic('caiInitialChatMessage')
      })
    },
    handleStorageChange(event) {
      if (event.key === 'chatHistory') {
        if (event.newValue === null) {
          this.messages = [{
            dialogId: null,
            sender: 'bot',
            text: this.tStatic('caiInitialChatMessage')
          }]
        } else {
          this.loadMessages()
        }
        this.scrollToBottom()
      } else if (event.key === 'sessionId') {
        this.currentSessionId = event.newValue

        if (event.newValue === null) {
          this.currentSessionId = null
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">

.floating-button {
  z-index: 2147483641;
  width: 3.25rem;
  height: 3.25rem;
  position: fixed;
  bottom: calc(1.25rem + env(safe-area-inset-bottom));
  right: 1.25rem;
  font-size: 24px;
  background-color: color(button-background-blue-dark);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: color(button-action-active);
  }
}

.unread-indicator {
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background-color: red;
  border-radius: 50%;
  border: 2px solid white;
}

.main {
  font-size: 0.875rem;
}

.chatbot {
  display: flex;
  z-index: 2147483641;
  flex-direction: column;
  width: 390px;
  height: calc(100vh - 150px);
  max-height: 700px;
  position: fixed;
  bottom: 20px;
  right: 20px;
  overflow: hidden;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 7px 6px 1px rgba(0, 0, 0, .16);
  animation: fadeInFromBottom 0.3s ease-out;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 60px;
  padding: 0.625rem 1.25rem;
  background-color: #1c508b;
  border: 0;
  color: white;

  i {
    cursor: pointer;
  }

  h5 {
    font-size: 1.25rem;
    margin-bottom: 0;
    font-weight: 700;
  }

  .actions {
    display: flex;

    button {
      display: flex;
      background: none;
      border: none;
      cursor: pointer;

      &:not(:last-child) {
        padding-right: 1rem;
      }
    }
  }
}

.logo-title-wrapper {
  display: flex;
  align-items: center;

  img {
    border-radius: 12px;
    margin-right: 10px;
  }
}

.body {
  height: 80%;
  padding: 12px;
  background-color: #fff;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.message-container {
  display: flex;
  flex-direction: column;
}

.message {
  padding: 12px;
  border-radius: 8px;
  text-align: left;
  font-size: 14px;
}

.message {
  &.prompt {
    max-width: 80%;
    background-color: #0073bb;
    color: #fff;
    align-self: flex-end;
  }

  &.response {
    background-color: #f2f3f3;
    color: #000;
    align-self: flex-start;
  }
}

.message-options {
  margin-top: 0.625rem;
}

.loading-text {
  position: relative;
  overflow: hidden;
  background: linear-gradient(
      90deg,
      #f0f0f0 25%,
      #e0e0e0 37%,
      #f0f0f0 63%
  );
  background-size: 400% 100%;
  animation: shimmer 3s linear infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.typing-dots {
  display: inline-flex;
  align-items: flex-end;
  margin-right: 8px;
  height: 12px;
}

.dot {
  width: 6px;
  height: 6px;
  margin: 0 2px;
  border-radius: 50%;
  background-color: #333;
  animation: fall 1s infinite ease-in-out;
  transform-origin: center bottom;
}

.dot:nth-child(1) {
  animation-delay: 0s;
}
.dot:nth-child(2) {
  animation-delay: 0.15s;
}
.dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes fall {
  0% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(4px);
    opacity: 1;
  }
  60% {
    transform: translateY(0);
    opacity: 0.6;
  }
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
}

.footer {
  display: flex;
  align-items: center;
  padding: 10px;
  border-top: 1px solid #ccc;
  background-color: #fff;
}

.input-field {
  display: flex;
  width: 100%;

  &[disabled] {
    pointer-events: none;
    cursor: not-allowed;
    background-color: #ccc;
  }
}

.message-input {
  flex-grow: 1;
  line-height: 1.5;
  padding: 10px 40px 10px 10px;
  border: 2px solid #ccc;
  border-radius: 0.5rem;
  transition: border-color 0.3s ease;
  font-size: 14px;
  background-color: transparent;
  resize: none;
  field-sizing: content;
  max-height: 100px;

  &:focus-visible {
    outline-color: #0073bb;
  }

  &::placeholder {
    color: #888;
  }

  &[disabled] {
    background-color: #eaeaea;
    cursor: not-allowed;
  }
}

.send-button {
  position: absolute;
  right: 12px;
  bottom: 15px;
  color: #ccc;
  height: 35px;
  width: 35px;
  margin-left: 10px;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 5px;

  &:hover {
    border-color: #0073bb;
  }

  &[disabled] {
    background-color: #eaeaea;
    cursor: not-allowed;
  }
}

::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #e7e4e4;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.visually-hidden {
  display: none;
}

@media (max-width: 480px) {
  .floating-button {
    bottom: 10px;
    right: 10px;
    font-size: 20px;
  }

  .chatbot {
    width: calc(100% - 20px);
    height: calc(100% - 20px);
    bottom: 10px;
    right: 10px;
    border-radius: 8px;
    animation: fadeInFromBottom 0.3s ease-out;
  }
}

@keyframes fadeInFromBottom {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>

<style lang="scss">
.message.response {
  ol, ul {
    margin-top: 0;
    margin-bottom: 1rem;
    padding-left: 2rem;
  }
}
</style>
